import { api, endpoints } from "api/api";
import { AxiosPromise } from "axios";
import { CourseSessionApi } from "./courseSessionsApi";

const fetchById = (courseId: number, courseScheduleId: number) => {
  return api.get<CourseScheduleDetailsApi>(endpoints.COURSESSCHEDULES.GET_BY_ID(courseId, courseScheduleId));
};

const deleteCourseSchedule = (courseId: number, courseScheduleId: number): AxiosPromise => {
  return api.delete(endpoints.COURSESSCHEDULES.DELETE(courseId, courseScheduleId));
};

const createTopic = (courseId: number, data: CreateTopicCourseScheduleRequest): AxiosPromise => {
  return api.post(endpoints.COURSESSCHEDULES.CREATE_TOPIC(courseId), data);
};
const updateTopic = (
  courseId: number,
  courseScheduleId: number,
  data: CreateTopicCourseScheduleRequest,
): AxiosPromise => {
  return api.put(endpoints.COURSESSCHEDULES.UPDATE_TOPIC(courseId, courseScheduleId), data);
};
const createTopicGroup = (courseId: number, data: CreateTopicGroupCourseScheduleRequest): AxiosPromise => {
  return api.post(endpoints.COURSESSCHEDULES.CREATE_TOPIC_GROUP(courseId), data);
};
const updateTopicGroup = (
  courseId: number,
  courseScheduleId: number,
  data: CreateTopicGroupCourseScheduleRequest,
): AxiosPromise => {
  return api.put(endpoints.COURSESSCHEDULES.UPDATE_TOPIC_GROUP(courseId, courseScheduleId), data);
};
const createBreak = (courseId: number, data: CreateBreakCourseScheduleRequest): AxiosPromise => {
  return api.post(endpoints.COURSESSCHEDULES.CREATE_BREAK(courseId), data);
};
const updateBreak = (
  courseId: number,
  courseScheduleId: number,
  data: CreateBreakCourseScheduleRequest,
): AxiosPromise => {
  return api.put(endpoints.COURSESSCHEDULES.UPDATE_BREAK(courseId, courseScheduleId), data);
};

const fetchPreview = (courseId: number) => {
  return api.get<CourseSchedulePreviewResponse>(`admin/courses/${courseId}/schedules/preview`);
};

const bulkUpdatePlaces = (courseId: number, data: BulkUpdatePlacesRequest): AxiosPromise => {
  return api.patch(endpoints.COURSESSCHEDULES.BULK_UPDATE_PLACES(courseId), data);
};

const bulkDelete = (courseId: number, data: BulkDeleteCourseSchedulesRequest): AxiosPromise => {
  return api.delete(endpoints.COURSESSCHEDULES.BULK_DELETE(courseId), { data });
};

const fetchHistory = (courseId: number): AxiosPromise<CourseScheduleActivityApi[]> => {
  return api.get<CourseScheduleActivityApi[]>(endpoints.COURSESSCHEDULES.HISTORY(courseId));
};

/** A trainer or on-site room also booked by another entry at an overlapping time (any course). */
export interface ScheduleConflictApi {
  scheduleId: number | null;
  kind: "trainer" | "place";
  resource: string;
  otherScheduleId: number;
  otherCourseId: number;
  otherCourseName: string;
  otherName: string;
  day: string;
  timeFrom: string;
  timeTo: string;
}

export interface CheckScheduleConflictsRequest {
  day: Date | string;
  timeFrom: string;
  timeTo: string;
  placeId?: number | null;
  placeType?: number | null;
  trainerIds: number[];
  excludeScheduleId?: number;
}

const fetchConflicts = (courseId: number): AxiosPromise<ScheduleConflictApi[]> =>
  api.get<ScheduleConflictApi[]>(endpoints.COURSESSCHEDULES.CONFLICTS(courseId));

const checkConflicts = (courseId: number, data: CheckScheduleConflictsRequest): AxiosPromise<ScheduleConflictApi[]> =>
  api.post<ScheduleConflictApi[]>(endpoints.COURSESSCHEDULES.CHECK_CONFLICTS(courseId), data);

export const courseschedulesApi = {
  fetchConflicts,
  checkConflicts,
  createTopic,
  updateTopic,
  updateBreak,
  fetchById,
  fetchPreview,
  createTopicGroup,
  updateTopicGroup,
  createBreak,
  bulkUpdatePlaces,
  bulkDelete,
  delete: deleteCourseSchedule,
  fetchHistory,
};
// TODO Is still used?
export interface CourseSchedulesResponse {
  data: CourseScheduleApi[];
  isReady: boolean;
}

export interface CreateBreakCourseScheduleRequest {
  day: Date;
  timeFrom: string;
  timeTo: string;
  courseSessionId: number;
}
export interface CreateTopicCourseScheduleRequest {
  day: Date;
  timeFrom: string;
  timeTo: string;
  courseSessionId: number;
  topicId: number;
  placeType: number;
  placeId?: number;
  trainers?: {
    trainerId: number;
    totalPrice: number;
  }[];
}
export interface CreateTopicGroupCourseScheduleRequest {
  day: Date;
  timeFrom: string;
  timeTo: string;
  courseSessionId: number;
  topicGroupId: number;
  placeType: number;
  placeId?: number;
  trainers?: {
    trainerId: number;
    totalPrice: number;
  }[];
}

export interface BulkUpdatePlacesRequest {
  courseScheduleIds: number[];
  placeType: number;
  placeId?: number;
}

export interface BulkDeleteCourseSchedulesRequest {
  courseScheduleIds: number[];
}

export interface CourseScheduleDetailsApi {
  schedule: CourseScheduleApi;
  trainers: { trainerId: number; totalPrice: number }[];
}
export interface CourseScheduleApi {
  id: number;
  dateFrom: Date;
  dateTo: Date;
  timeFrom: string;
  timeTo: string;
  session: CourseSessionApi;
  placeType: number;
  placeName?: string;
  placeId?: number;
  topicName?: string;
  topicId?: number;
  topicGroupName?: string;
  topicGroupId?: number;
  examName?: string;
  examId?: number;
}

export interface CourseScheduleActivityApi {
  id: number;
  description: string;
  createdDate: Date;
  author: {
    userId: number;
    email: string;
  };
}

export interface CourseSchedulePreviewResponse {
  scheduleItems: CourseSchedulePreviewItem[];
  totalDuration: number;
}

export interface CourseSchedulePreviewItem {
  name: string;
  timeFrom: string;
  timeTo: string;
  duration: number;
}
